import type { ChangeEvent, FocusEvent, TextareaHTMLAttributes } from 'react';
import { useFieldControl } from '../Field/fieldContext';
import styles from './Textarea.module.css';

/*
 * `className` and `disabled` are omitted from the native attributes and re-declared, for the reasons
 * given on `Input`: the look is this component's own, and the bare `disabled` attribute is never
 * rendered.
 */
type NativeTextareaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className' | 'disabled'>;

export interface TextareaProps extends NativeTextareaProps {
    /** Rendered as `aria-disabled` + `readOnly` — see the component body. */
    readonly disabled?: boolean;
    /** Marks the control invalid on its own. Inside a `Field` the field supplies it with its message. */
    readonly invalid?: boolean;
    /** The visible line count before the box scrolls. Default 5. */
    readonly rows?: number;
    /** The test hook (`coding-standards.md §4`), forwarded to the real `<textarea>`. */
    readonly 'data-testid'?: string | undefined;
}

/**
 * §5's multi-line text field — a real `<textarea>` in the Input family: the same border, radius, field
 * surface, body type and states as `Input`, for text that runs to several lines (an inquiry pasted
 * from an email, a note) — F11-b WI-1.3 plan Decision 9; design-system §5 *Textarea*, Rev 38.
 *
 * - **Wired to `Field` through `useFieldControl`**, exactly as `Input` is: the label's `for`, the
 *   message's `aria-describedby`, `aria-invalid`, and the blur and change reports that drive §5's
 *   validation timing. Outside a `Field` it is an ordinary textarea with its own props untouched.
 * - **Disabled is `aria-disabled` plus `readOnly`, never the bare `disabled` attribute** (§5's matrix,
 *   §9's permission rule): the control stays focusable so its reason can be found, and `readOnly` is
 *   what actually stops the typing. A change arriving anyway is refused before the caller sees it.
 * - **It grows down, never sideways**: `rows` sets the starting height (5 by default) and the user may
 *   resize it vertically only, so a resize can never push the form wider than its column.
 */
export function Textarea({
    disabled = false,
    invalid,
    rows = 5,
    id,
    required,
    'aria-describedby': describedBy,
    'data-testid': testId,
    onBlur,
    onChange,
    ...rest
}: TextareaProps) {
    const field = useFieldControl({ id, 'aria-describedby': describedBy, invalid, required });

    function handleBlur(event: FocusEvent<HTMLTextAreaElement>) {
        onBlur?.(event);
        field.reportBlur(event.target.value);
    }

    function handleChange(event: ChangeEvent<HTMLTextAreaElement>) {
        if (disabled) {
            return;
        }
        onChange?.(event);
        field.reportChange(event.target.value);
    }

    return (
        <textarea
            {...rest}
            aria-describedby={field['aria-describedby']}
            aria-disabled={disabled}
            aria-invalid={field['aria-invalid']}
            className={styles.textarea}
            data-testid={testId}
            id={field.id}
            onBlur={handleBlur}
            onChange={handleChange}
            readOnly={disabled || rest.readOnly}
            required={field.required}
            rows={rows}
        />
    );
}
